/**
 * ISO 8601 and US week numbering, on plain day counts.
 *
 * Dates are counted as days since 1970-01-01 in the proleptic Gregorian calendar, with
 * Howard Hinnant's civil-from-days arithmetic rather than `Date.UTC`, which silently maps
 * the years 0–99 to 1900–1999. No clock and no time zone is involved, so no daylight
 * saving rule can move a date across midnight.
 *
 * ISO 8601 week dates:
 * weeks start on Monday, and week 01 of a year is the week that contains that year's
 * first Thursday — equivalently, the week containing 4 January. Every week therefore
 * belongs to the year of its Thursday, which is why 1 January can fall in week 52 or 53
 * of the year before and 29–31 December in week 01 of the year after. A year has 53 weeks
 * when it starts on a Thursday, or is a leap year starting on a Wednesday.
 *
 * The US convention used by most American wall calendars and by Excel's
 * `WEEKNUM(date, 1)`: weeks start on Sunday and week 1 is the week containing 1 January.
 * Days are numbered within their own calendar year, so the Sunday–Saturday week that
 * straddles New Year is the last week of one year up to 31 December and week 1 of the
 * next from 1 January. That makes 53 weeks normal and 54 possible (a leap year that
 * starts on a Saturday, such as 2000 or 2028).
 */

export const MIN_YEAR = 1583;
export const MAX_YEAR = 9999;

export interface CivilDate {
  year: number;
  month: number;
  day: number;
}

/** Days since 1970-01-01 for a proleptic Gregorian date. */
export function daysFromCivil(
  year: number,
  month: number,
  day: number
): number {
  const y = month <= 2 ? year - 1 : year;
  const era = Math.floor(y / 400);
  const yearOfEra = y - era * 400;
  const monthFromMarch = (month + 9) % 12;
  const dayOfYear = Math.floor((153 * monthFromMarch + 2) / 5) + day - 1;
  const dayOfEra =
    yearOfEra * 365 +
    Math.floor(yearOfEra / 4) -
    Math.floor(yearOfEra / 100) +
    dayOfYear;
  return era * 146_097 + dayOfEra - 719_468;
}

/** The proleptic Gregorian date of a day count. */
export function civilFromDays(days: number): CivilDate {
  const z = days + 719_468;
  const era = Math.floor(z / 146_097);
  const dayOfEra = z - era * 146_097;
  const yearOfEra = Math.floor(
    (dayOfEra -
      Math.floor(dayOfEra / 1460) +
      Math.floor(dayOfEra / 36_524) -
      Math.floor(dayOfEra / 146_096)) /
      365
  );
  const dayOfYear =
    dayOfEra -
    (365 * yearOfEra + Math.floor(yearOfEra / 4) - Math.floor(yearOfEra / 100));
  const monthFromMarch = Math.floor((5 * dayOfYear + 2) / 153);
  const day = dayOfYear - Math.floor((153 * monthFromMarch + 2) / 5) + 1;
  const month = monthFromMarch < 10 ? monthFromMarch + 3 : monthFromMarch - 9;
  const year = yearOfEra + era * 400 + (month <= 2 ? 1 : 0);
  return { year, month, day };
}

export function isLeapYear(year: number): boolean {
  return (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;
}

export function daysInYear(year: number): number {
  return isLeapYear(year) ? 366 : 365;
}

/** 1 = Monday … 7 = Sunday. */
export function isoWeekday(days: number): number {
  return ((((days + 3) % 7) + 7) % 7) + 1;
}

/** 0 = Sunday … 6 = Saturday. */
export function sundayWeekday(days: number): number {
  return (((days + 4) % 7) + 7) % 7;
}

/** 1 for 1 January. */
export function dayOfYear(days: number): number {
  const { year } = civilFromDays(days);
  return days - daysFromCivil(year, 1, 1) + 1;
}

export interface IsoWeek {
  weekYear: number;
  week: number;
  /** 1 = Monday … 7 = Sunday. */
  weekday: number;
}

export function isoWeek(days: number): IsoWeek {
  const weekday = isoWeekday(days);
  const thursday = days + (4 - weekday);
  const weekYear = civilFromDays(thursday).year;
  const week = Math.floor((dayOfYear(thursday) - 1) / 7) + 1;
  return { weekYear, week, weekday };
}

/** 52 or 53: the week that 28 December falls in is always the year's last. */
export function isoWeeksInYear(year: number): number {
  return isoWeek(daysFromCivil(year, 12, 28)).week;
}

/** The Monday of an ISO week (the week containing 4 January is week 1). */
export function isoWeekMonday(weekYear: number, week: number): number {
  const january4 = daysFromCivil(weekYear, 1, 4);
  return january4 - (isoWeekday(january4) - 1) + (week - 1) * 7;
}

/** `2026-W53-5`. */
export function formatIsoWeekDate(week: IsoWeek): string {
  return `${week.weekYear}-W${String(week.week).padStart(2, "0")}-${week.weekday}`;
}

export interface UsWeek {
  year: number;
  week: number;
  /** 1 = Sunday … 7 = Saturday. */
  weekday: number;
}

/** The US week number — Excel's `WEEKNUM(date, 1)`. */
export function usWeek(days: number): UsWeek {
  const { year } = civilFromDays(days);
  const january1 = daysFromCivil(year, 1, 1);
  const offset = sundayWeekday(january1);
  return {
    year,
    week: Math.floor((days - january1 + offset) / 7) + 1,
    weekday: sundayWeekday(days) + 1,
  };
}

/** 53 or 54: the US week number of 31 December. */
export function usWeeksInYear(year: number): number {
  return usWeek(daysFromCivil(year, 12, 31)).week;
}

export interface WeekSpan {
  week: number;
  /** First and last day of the week as numbered in this year. */
  start: number;
  end: number;
  /** The full seven-day week, which may reach into the next or previous year. */
  fullStart: number;
  fullEnd: number;
}

export function isoWeekSpan(weekYear: number, week: number): WeekSpan {
  const monday = isoWeekMonday(weekYear, week);
  return {
    week,
    start: monday,
    end: monday + 6,
    fullStart: monday,
    fullEnd: monday + 6,
  };
}

/** A US week, clipped to the calendar year that numbers it. */
export function usWeekSpan(year: number, week: number): WeekSpan {
  const january1 = daysFromCivil(year, 1, 1);
  const sunday = january1 - sundayWeekday(january1) + (week - 1) * 7;
  const december31 = daysFromCivil(year, 12, 31);
  return {
    week,
    start: Math.max(sunday, january1),
    end: Math.min(sunday + 6, december31),
    fullStart: sunday,
    fullEnd: sunday + 6,
  };
}

export type WeekSystem = "iso" | "us";

export function weeksInYear(system: WeekSystem, year: number): number {
  return system === "iso" ? isoWeeksInYear(year) : usWeeksInYear(year);
}

export function weekSpan(
  system: WeekSystem,
  year: number,
  week: number
): WeekSpan {
  return system === "iso" ? isoWeekSpan(year, week) : usWeekSpan(year, week);
}

/** Every week of a year in one numbering, in order. */
export function weeksOfYear(system: WeekSystem, year: number): WeekSpan[] {
  const count = weeksInYear(system, year);
  const weeks: WeekSpan[] = [];
  for (let week = 1; week <= count; week++) {
    weeks.push(weekSpan(system, year, week));
  }
  return weeks;
}

const ISO_DATE = /^(\d{4})-(\d{2})-(\d{2})$/;

/** `YYYY-MM-DD` as a day count, or null when it is not a real date in range. */
export function parseIsoDate(text: string): number | null {
  const match = ISO_DATE.exec(text.trim());
  if (!match) {
    return null;
  }
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  if (year < MIN_YEAR || year > MAX_YEAR || month < 1 || month > 12) {
    return null;
  }
  const days = daysFromCivil(year, month, day);
  const back = civilFromDays(days);
  return back.month === month && back.day === day ? days : null;
}

/** A day count as `YYYY-MM-DD`. */
export function toIsoDate(days: number): string {
  const { year, month, day } = civilFromDays(days);
  return `${String(year).padStart(4, "0")}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
}

/** Midnight UTC of a day count, for `Intl.DateTimeFormat` with `timeZone: "UTC"`. */
export function dayToUtcDate(days: number): Date {
  return new Date(days * 86_400_000);
}
